<script setup lang="ts">
import { onMounted } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { handleError } from "@/appdata/globals";
import SettingRulePicker from "@/components/atoms/SettingRulePicker.vue";
import type { SettingRuleInterface } from "@/types/worlds";

const ux = useUxData();
const setupStore = useGuidedSetupStore();

// The selected sources (in the store) seed the filter scope. predicateStore.sources is derived
// working state: the WorldFinder appends to it, removing a chip narrows it, and mounting reseeds
// it from the selection (so ad-hoc additions reset when the user leaves and returns).
const seededWorlds = () => setupStore.selectedSources.map((s) => s.world);

const isRuleAdded = (rule: SettingRuleInterface): boolean =>
    (setupStore.worldDraft?.settingRules ?? []).some((r) => r.uuid === rule.uuid);

const toggleRule = (rule: SettingRuleInterface) => {
    if (isRuleAdded(rule)) setupStore.removeSettingRule(rule);
    else setupStore.addSettingRule(rule);
};

onMounted(() => {
    setupStore.validateStep(2, true);
    // Auto-add each selected source's setting rules to the draft (seeded once per source, so
    // manual removals survive a re-visit). The slim source world carries no settingRules, so the
    // store reads them from the source's full world.
    setupStore.seedSettingRulesFromSources().catch(handleError);
});
</script>

<template>
  <div class="world-rules-step">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold">
        {{ ux.labels?.swade?.data?.settingRules?.header ?? 'Setting Rules' }}
      </label>
      <p class="form-text mt-0 mb-3" v-html="ux.labels?.guidedSetup?.world?.rulesHelp"></p>

      <SettingRulePicker
        :selected="(setupStore.worldDraft?.settingRules ?? []).map((r) => r.uuid ?? '')"
        :seed-sources="seededWorlds()"
        @toggle="toggleRule"
      />
    </div>
  </div>
</template>
